import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Button, Flex, Icon } from '~/components/blocks';
import { SelectDate } from '~/components/partials';
import { toYYYYMMDDhyphen } from '~/utils/date';

type Props = {
  disabled?: boolean;
  clearable?: boolean;
  value: Date | null;
  maxDate?: Date | null;
  minDate?: Date | null;
  onChange: (date: Date | null) => void;
};

const MoveButton = styled(Button)(({ theme }) =>
  css({
    padding: `${theme.space.s} ${theme.space.m}`,
    '&:disabled': {
      backgroundColor: theme.colors.colorPallete.grey03,
    },
  }),
);

export const SelectDateWithMoveButton = React.memo((props: Props) => {
  const { clearable = false, value, onChange, disabled, maxDate, minDate } = props;
  const theme = useTheme();
  const disablePrev = !value || (!!minDate && toYYYYMMDDhyphen(value) == toYYYYMMDDhyphen(minDate));
  const disableNext = !value || (!!maxDate && toYYYYMMDDhyphen(value) == toYYYYMMDDhyphen(maxDate));
  const isTablet = useMediaQuery({ query: theme.mediaQueries.tablet });

  const handleClickPrev = useCallback(() => {
    if (!value) return;

    const prevDate = new Date(value.getTime());
    prevDate.setDate(value.getDate() - 1);
    props.onChange(prevDate);
  }, [props, value]);

  const handleClickNext = useCallback(() => {
    if (!value) return;

    const nextDate = new Date(value.getTime());
    nextDate.setDate(value.getDate() + 1);
    props.onChange(nextDate);
  }, [props, value]);

  return (
    <Flex height={isTablet ? '34px' : undefined}>
      <MoveButton icon marginRight={theme.space.s} onClick={handleClickPrev} disabled={disablePrev}>
        <Icon icon="arrow-left" size="l" />
      </MoveButton>
      <SelectDate
        clearable={clearable}
        disabled={disabled}
        value={value}
        onChange={onChange}
        maxDate={maxDate}
        minDate={minDate}
      />
      <MoveButton icon marginLeft={theme.space.s} onClick={handleClickNext} disabled={disableNext}>
        <Icon icon="arrow-right" size="l" />
      </MoveButton>
    </Flex>
  );
});

SelectDateWithMoveButton.displayName = 'SelectDateWithMoveButton';
